import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import differenceInDays from 'date-fns/differenceInDays';
import startOfToday from 'date-fns/startOfToday';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import type { OnCloseHandler, OnOpenCompleteHandler } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import {
	type AnalyticsAttributes,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/analytics-bridge';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import type { ProductKey } from '../../types';

import { FeatureComparisonPanel } from './feature-comparison-panel';
import { messages } from './messages';
import { PriceContrast } from './price-contrast';

export interface RecommendStandardOverPremiumModalProps {
	/**
	 * Whether to render the modal as customised for Jira or Confluence.
	 */
	productKey: ProductKey;

	/**
	 * The price per user per month of the Standard edition.
	 */
	standardPrice: number;

	/**
	 * The price per user per month of the Premium edition.
	 */
	premiumPrice: number;

	/**
	 * The date the trial will end. Make sure this date is correct in the browser's local timezone.
	 */
	trialEndDate: Date;

	/**
	 * Used to populate the screen event that this modal will fire on render.
	 */
	analyticsAttributes: AnalyticsAttributes;

	/**
	 * Callback function called when the modal dialog is requesting to be closed.
	 */
	onClose: OnCloseHandler;

	/**
	 * Callback function called when the user clicks the primary CTA to buy Standard.
	 */
	onClickBuyStandard: () => void;

	/**
	 * Callback function called when the modal dialog has finished opening. Use this to execute any other
	 * code you want such as firing experience tracker events.
	 */
	onOpenComplete?: OnOpenCompleteHandler;

	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 * If not overridden using `testId` prop in the respective components, this will set `data-testid` on these elements when defined:
	 * - Modal dialog: `{testId}`
	 * - Modal header: `{testId}--header`
	 * - Modal title: `{testId}--title`
	 * - Modal body: `{testId}--body`
	 * - Modal footer: `{testId}--footer`
	 * - Scrollable element: `{testId}--scrollable`
	 * - Blanket: `{testId}--blanket`
	 */
	testId?: string;
}

/**
 * A two-panel modal with UI matching these designs:
 * https://www.figma.com/design/gN2uHK0wPD93kkqExPlNeM/Confluence%3A-Recommend-Standard-to-RT-trialing-customers-(RT)?node-id=1-3&node-type=canvas&t=ZS7oExyVxcrlRkJQ-0
 *
 * This is intended to be used by Confluence as well as the Jira versions of the experiment.
 */
export const RecommendStandardOverPremiumModal = ({
	productKey,
	standardPrice,
	premiumPrice,
	trialEndDate,
	analyticsAttributes,
	onClose,
	onClickBuyStandard,
	onOpenComplete,
	testId,
}: RecommendStandardOverPremiumModalProps) => {
	const { formatMessage } = useIntl();

	const percentageOff = Math.round(100 * (1 - standardPrice / premiumPrice));
	const daysLeftInTrial = differenceInDays(trialEndDate, startOfToday()) + 1;

	return (
		<ContextualAnalyticsData
			sourceName="recommendStandardOverPremiumModal"
			sourceType={MODAL}
			attributes={analyticsAttributes}
		>
			<Modal
				testId={testId}
				onClose={onClose}
				onOpenComplete={onOpenComplete}
				title={
					<FormattedMessage
						{...messages.title}
						values={{
							purple: (chunks) => <Purple>{chunks}</Purple>,
							percentageOff,
						}}
					/>
				}
				subtitle={formatMessage(messages.subtitle, { daysLeftInTrial })}
				height="536px"
				right={
					<FeatureComparisonPanel
						productKey={productKey}
						standardPrice={standardPrice}
						premiumPrice={premiumPrice}
					/>
				}
				actions={
					<>
						<Button appearance="subtle" onClick={onClose}>
							{formatMessage(messages.maybeLater)}
						</Button>
						<Button appearance="primary" onClick={onClickBuyStandard}>
							{formatMessage(messages.buyStandard)}
						</Button>
					</>
				}
			>
				<PriceContrast standardPrice={standardPrice} premiumPrice={premiumPrice} />
				<FireScreenAnalytics />
			</Modal>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Purple = styled.span({
	color: token('color.icon.accent.purple'),
});
